/**
 * Tag Ownership Utility
 * 
 * Manages tag visibility by updating the owner_ids array on crm_tags.
 * When a tag is applied to a contact/deal, the data owner is added to 
 * the tag's owner_ids, allowing them to see and filter by that tag.
 * 
 * This ensures:
 * - Users can filter by tags on their data, even if they didn't create the tag
 * - When data is reassigned, the new owner can filter by existing tags
 */

import { supabase, isSupabaseConfigured } from './supabase';

/**
 * Update tag ownership for a single tag
 * Adds the data owner to the tag's owner_ids array (if not already present)
 * 
 * @param {string} tagValue - The tag value
 * @param {string} entityType - 'contact' or 'deal'
 * @param {string} dataOwnerId - Firebase UID of the contact/deal owner
 * @returns {Promise<boolean>} - True if successful
 */
export async function updateTagOwnership(tagValue, entityType, dataOwnerId) {
  if (!isSupabaseConfigured() || !tagValue || !entityType || !dataOwnerId) {
    return false;
  }

  try {
    // Call the database function to add owner
    const { error } = await supabase.rpc('add_tag_owner', {
      p_tag_value: tagValue,
      p_entity_type: entityType,
      p_user_id: dataOwnerId,
    });

    if (error) {
      // If the function doesn't exist yet (migration not run), fall back to direct update
      if (error.code === '42883') {
        console.warn('add_tag_owner function not found, using fallback');
        return await updateTagOwnershipFallback(tagValue, entityType, dataOwnerId);
      }
      console.error('Error updating tag ownership:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error in updateTagOwnership:', err);
    return false;
  }
}

/**
 * Fallback implementation if the database function doesn't exist yet
 */
async function updateTagOwnershipFallback(tagValue, entityType, dataOwnerId) {
  try {
    // First check if the tag exists and is not global
    const { data: existingTag, error: fetchError } = await supabase
      .from('crm_tags')
      .select('id, visibility, owner_ids')
      .eq('value', tagValue)
      .eq('entity_type', entityType)
      .eq('is_active', true)
      .single();

    if (fetchError || !existingTag) {
      // Tag doesn't exist in crm_tags - that's OK, it might be an orphan tag
      return true;
    }

    // Skip global tags
    if (existingTag.visibility === 'global') {
      return true;
    }

    // Check if user is already in owner_ids
    const currentOwnerIds = existingTag.owner_ids || [];
    if (currentOwnerIds.includes(dataOwnerId)) {
      return true; // Already has access
    }

    // Add user to owner_ids
    const { error: updateError } = await supabase
      .from('crm_tags')
      .update({ 
        owner_ids: [...currentOwnerIds, dataOwnerId],
        updated_at: new Date().toISOString(),
      })
      .eq('id', existingTag.id);

    if (updateError) {
      console.error('Error in updateTagOwnershipFallback:', updateError);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error in updateTagOwnershipFallback:', err);
    return false;
  }
}

/**
 * Update tag ownership for multiple tags (batch operation)
 * More efficient than calling updateTagOwnership repeatedly
 * 
 * @param {string[]} tagValues - Array of tag values
 * @param {string} entityType - 'contact' or 'deal'
 * @param {string} dataOwnerId - Firebase UID of the contact/deal owner
 * @returns {Promise<number>} - Number of tags updated
 */
export async function updateTagOwnershipBatch(tagValues, entityType, dataOwnerId) {
  if (!isSupabaseConfigured() || !tagValues?.length || !entityType || !dataOwnerId) {
    return 0;
  }

  try {
    // Call the database function to add owner to multiple tags
    const { data, error } = await supabase.rpc('add_tag_owner_batch', {
      p_tag_values: tagValues,
      p_entity_type: entityType,
      p_user_id: dataOwnerId,
    });

    if (error) {
      // If the function doesn't exist yet, fall back to individual updates
      if (error.code === '42883') {
        console.warn('add_tag_owner_batch function not found, using fallback');
        let count = 0;
        for (const tagValue of tagValues) {
          if (await updateTagOwnershipFallback(tagValue, entityType, dataOwnerId)) {
            count++;
          }
        }
        return count;
      }
      console.error('Error in updateTagOwnershipBatch:', error);
      return 0;
    }

    return data || 0;
  } catch (err) {
    console.error('Error in updateTagOwnershipBatch:', err);
    return 0;
  }
}

/**
 * Sync tag ownership when entities are reassigned
 * Adds the new owner to owner_ids for all tags on the specified entities
 * 
 * @param {string} entityType - 'contact' or 'deal'
 * @param {string[]} entityIds - Array of contact or deal UUIDs
 * @param {string} newOwnerId - Firebase UID of the new owner
 * @returns {Promise<number>} - Number of tags updated
 */
export async function syncTagOwnershipOnReassignment(entityType, entityIds, newOwnerId) {
  if (!isSupabaseConfigured() || !entityIds?.length || !entityType || !newOwnerId) {
    return 0;
  }

  try {
    // Call the database function
    const { data, error } = await supabase.rpc('sync_tag_ownership_on_reassignment', {
      p_entity_type: entityType,
      p_entity_ids: entityIds,
      p_new_owner_id: newOwnerId,
    });

    if (error) {
      // If the function doesn't exist yet, fall back to manual approach
      if (error.code === '42883') {
        console.warn('sync_tag_ownership_on_reassignment function not found, using fallback');
        return await syncTagOwnershipFallback(entityType, entityIds, newOwnerId);
      }
      console.error('Error in syncTagOwnershipOnReassignment:', error);
      return 0;
    }

    return data || 0;
  } catch (err) {
    console.error('Error in syncTagOwnershipOnReassignment:', err);
    return 0;
  }
}

/**
 * Fallback implementation for syncTagOwnershipOnReassignment
 */
async function syncTagOwnershipFallback(entityType, entityIds, newOwnerId) {
  try {
    const tableName = entityType === 'contact' ? 'crm_contacts' : 'crm_deals';
    
    // Fetch all tags from the entities
    const { data: entities, error: fetchError } = await supabase
      .from(tableName)
      .select('tags')
      .in('id', entityIds);

    if (fetchError) {
      console.error('Error fetching entity tags:', fetchError);
      return 0;
    }

    // Collect all unique tags
    const allTags = new Set();
    entities?.forEach(entity => {
      (entity.tags || []).forEach(tag => allTags.add(tag));
    });

    if (allTags.size === 0) {
      return 0;
    }

    // Update ownership for all tags
    return await updateTagOwnershipBatch(Array.from(allTags), entityType, newOwnerId);
  } catch (err) {
    console.error('Error in syncTagOwnershipFallback:', err);
    return 0;
  }
}

/**
 * Sync tag ownership for multiple owner/tag combinations
 * Used when bulk-adding tags to entities with different owners
 * 
 * @param {Array<{tagValue: string, ownerId: string}>} tagOwnerPairs - Pairs of tag values and owner IDs
 * @param {string} entityType - 'contact' or 'deal'
 * @returns {Promise<number>} - Number of updates performed
 */
export async function syncTagOwnershipForMultipleOwners(tagOwnerPairs, entityType) {
  if (!isSupabaseConfigured() || !tagOwnerPairs?.length || !entityType) {
    return 0;
  }

  try {
    // Group by owner for efficiency
    const tagsByOwner = new Map();
    tagOwnerPairs.forEach(({ tagValue, ownerId }) => {
      if (!ownerId) return;
      if (!tagsByOwner.has(ownerId)) {
        tagsByOwner.set(ownerId, new Set());
      }
      tagsByOwner.get(ownerId).add(tagValue);
    });

    let totalUpdated = 0;
    for (const [ownerId, tags] of tagsByOwner) {
      const count = await updateTagOwnershipBatch(Array.from(tags), entityType, ownerId);
      totalUpdated += count;
    }

    return totalUpdated;
  } catch (err) {
    console.error('Error in syncTagOwnershipForMultipleOwners:', err);
    return 0;
  }
}
